/**
 * 新闻管理模块 - 完整的CRUD功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, message, Form, DatePicker, Upload, Input, Space, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { newsService } from '../../../../../services/newsService';
import moment from 'moment';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import { DictSelect } from '../../../../../components/common/DictSelect';
import { DICT_TYPES, DEFAULT_DICT_DATA } from '../../../../../constants/dictTypes';
import { useDict } from '../../../../../hooks/useUnifiedDict';
import QueryArea, { QueryField } from '../../components/QueryArea';
import PictureUpload from '../../../../../components/common/PictureUpload';
import CustomRichTextEditor, { CustomRichTextEditorRef } from '../../../../../components/common/CustomRichTextEditor/index';
import AttachmentUpload, { AttachmentUploadRef } from '../../../../../components/common/AttachmentUpload';
import styles from './index.module.css';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface News {
  id: number;
  title: string;
  content: string;
  summary: string;
  category: string;
  author: string;
  publishTime: string;
  status: number;
  viewCount: number;
  tags: string[] | string; // 支持数组或字符串格式
  coverImage?: string;
  attachmentUrls?: string; // 附件URL列表（JSON数组字符串）
}

const NewsManagement: React.FC = () => {
  const [dataSource, setDataSource] = useState<News[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    category: '',
    status: '',
    author: '',
    publishTimeRange: null,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<News | null>(null);
  const editorContentRef = useRef<string>('');
  const attachmentUploadRef = useRef<AttachmentUploadRef>(null);
  const editorRef = useRef<CustomRichTextEditorRef>(null);

  // 使用数据字典
  const { getLabelByValue: getCategoryLabel, dictData: categoryDictData } = useDict(DICT_TYPES.NEWS_CATEGORY);
  const { getLabelByValue: getStatusLabel, dictData: statusDictData, loading: statusLoading } = useDict(DICT_TYPES.NEWS_STATUS);

  // 监听Modal打开，同步编辑器内容
  useEffect(() => {
    if (isModalVisible && editingRecord) {
      const content = form.getFieldValue('content');
      if (content && content !== editorContentRef.current) {
        editorContentRef.current = content;
      }
    }
  }, [isModalVisible, editingRecord, form]);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '标题',
      type: 'input',
      placeholder: '搜索标题或内容...',
      width: 200,
    },
    {
      key: 'category',
      label: '分类',
      type: 'dict',
      dictType: DICT_TYPES.NEWS_CATEGORY,
      placeholder: '选择分类',
      width: 150,
    },
    {
      key: 'status',
      label: '发布状态',
      type: 'dict',
      dictType: DICT_TYPES.NEWS_STATUS,
      placeholder: '选择发布状态',
      width: 120,
    },
    {
      key: 'author',
      label: '作者',
      type: 'input',
      placeholder: '搜索作者...',
      width: 150,
    },
    {
      key: 'publishTimeRange',
      label: '发布时间',
      type: 'dateRange',
      placeholder: '选择发布时间范围',
      width: 240,
    },
  ];

  // 表格列定义
  const columns: ColumnsType<News> = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 90,
      render: (value) => getCategoryLabel(String(value)) || value,
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 90,
    },
    {
      title: '发布时间',
      dataIndex: 'publishTime',
      key: 'publishTime',
      width: 170,
      render: (value) => value ? new Date(value).toLocaleString('zh-CN') : '-',
    },
    {
      title: '发布状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (value) => {
        // 将整型值转换为字符串以匹配字典中的dict_value
        const label = getStatusLabel(String(value));
        return (
        <span className={`status-tag ${value === 1 ? 'published' : 'draft'}`}>
            {label || (value === 1 ? '已发布' : '未发布')}
        </span>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 加载数据 - 使用useCallback优化，避免不必要的重新创建
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        current,
        size: pageSize,
        keyword: searchParams.keyword,
        category: searchParams.category,
        status: searchParams.status,
        author: searchParams.author,
        // 处理时间范围参数
        startTime: searchParams.publishTimeRange && searchParams.publishTimeRange[0] 
          ? (searchParams.publishTimeRange[0] as any).format('YYYY-MM-DD') 
          : undefined,
        endTime: searchParams.publishTimeRange && searchParams.publishTimeRange[1] 
          ? (searchParams.publishTimeRange[1] as any).format('YYYY-MM-DD') 
          : undefined,
      };
      
      const response = await newsService.getList(params);
      if (response.code === 200) {
        setDataSource(response.data.records || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchParams]);

  // 监听分页和搜索参数变化，自动加载数据
  useEffect(() => {
      loadData();
  }, [current, pageSize, searchParams, loadData]);

  // 搜索
  const handleSearch = () => {
    setCurrent(1);
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ 
      keyword: '', 
      category: '', 
      status: '', 
      author: '', 
      publishTimeRange: null 
    });
    setCurrent(1);
  };

  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      keyword: values.keyword || '',
      category: values.category || '',
      status: values.status || '',
      author: values.author || '',
      publishTimeRange: values.publishTimeRange || null,
    });
  }, []);

  // 新增
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    editorContentRef.current = '';
    form.setFieldsValue({
      status: '1',
      tags: [],
      coverImage: '',
      content: '',
    });
    setIsModalVisible(true);
  };

  // 编辑
  const handleEdit = (record: News) => {
    setEditingRecord(record);
    
    // 加载编辑器内容
    editorContentRef.current = record.content || '';
    
    form.setFieldsValue({
      ...record,
      publishTime: record.publishTime ? moment(record.publishTime) : null,
      // 处理状态字段 - 确保为字符串格式
      status: String(record.status || '0'),
      // 处理tags字段 - 如果是字符串则转换为数组
      tags: Array.isArray(record.tags) ? record.tags : (typeof record.tags === 'string' && record.tags ? record.tags.split(',') : []),
      // 处理coverImage字段 - 保持Base64字符串
      coverImage: record.coverImage || undefined,
      // 确保content字段设置
      content: record.content || '',
    });
    
    setIsModalVisible(true);
  };

  // 删除
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条新闻吗？删除后无法恢复。',
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await newsService.delete(id);
          if (response.code === 200) {
            message.success('删除成功');
            loadData();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条新闻吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await newsService.batchDelete(selectedRowKeys as number[]);
          if (response.code === 200) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            loadData();
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };



  // 表单提交
  const handleFormSubmit = async () => {
    try {
      
      const values = await form.validateFields();
      
      setLoading(true);
      
      // 上传附件（如果有pending状态的文件）
      let attachmentUrls = null;
      if (attachmentUploadRef.current) {
        
        // 上传所有pending状态的文件并获取所有URLs（包括已存在的）
        const allUrls = await attachmentUploadRef.current.uploadFiles();
        
        // 获取数据库中的原有附件URL（仅在编辑模式下）
        if (editingRecord) {
          const existingUrls = editingRecord.attachmentUrls 
            ? JSON.parse(editingRecord.attachmentUrls) 
            : [];
          
          // 找出需要删除的文件（数据库中有的但提交后没有的）
          const toDeleteUrls = existingUrls.filter((url: string) => !allUrls.includes(url));
          
          // 删除服务器上的多余文件
          for (const url of toDeleteUrls) {
            try {
              const fileName = url.split('/').pop() || '';
              
              const deleteResponse = await fetch(`/api/biz/news/deleteFile?fileName=${encodeURIComponent(fileName)}`, {
                method: 'DELETE',
              });
              
              if (deleteResponse.ok) {
                const result = await deleteResponse.json();
              } else {
                console.error('文件删除失败:', fileName);
              }
            } catch (error) {
              console.error('删除文件时出错:', error);
            }
          }
        }
        
        if (allUrls.length > 0) {
          attachmentUrls = JSON.stringify(allUrls);
        } else {
        }
      }
      
      // 先上传编辑器中的待上传文件（视频和附件）
      let editorContent = editorContentRef.current || '';
      if (editorRef.current) {
        try {
          editorContent = await editorRef.current.uploadPendingFiles();
          editorContentRef.current = editorContent;
        } catch (error) {
          console.error('上传待上传文件失败:', error);
          message.error('上传文件失败，请重试');
          return;
        }
      }
      
      const formData = {
        ...values,
        content: editorContent, // 使用自定义编辑器内容
        // 处理发布时间
        publishTime: values.publishTime ? values.publishTime.format('YYYY-MM-DD HH:mm:ss') : null,
        // 处理标签 - 确保不为空字符串
        tags: values.tags && Array.isArray(values.tags) && values.tags.length > 0 ? values.tags.join(',') : null,
        // 处理封面图片 - PictureUpload组件会自动管理coverImage字段
        coverImage: values.coverImage || null,
        // 附件URL列表
        attachmentUrls: attachmentUrls || null,
        // 确保状态有默认值
        status: values.status || 1,
        // 确保viewCount有默认值
        viewCount: values.viewCount || 0,
      };
      
      
      // 4. 提交表单数据
      let response;
      if (editingRecord) {
        response = await newsService.update({ ...formData, id: editingRecord.id });
      } else {
        response = await newsService.create(formData);
      }
      
      
      if (response.code === 200) {
        message.success(editingRecord ? '编辑成功' : '新增成功');
        setIsModalVisible(false);
        form.resetFields();
        editorContentRef.current = '';
        loadData();
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      console.error('错误详情:', error);
      message.error('提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="news-management">
      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        onBatchDelete={handleBatchDelete}
        batchDeleteDisabled={selectedRowKeys.length === 0}
        selectedCount={selectedRowKeys.length}
        addButtonText="新增"
        batchDeleteButtonText="批量删除"
      />

      {/* 数据表格 */}
      <div className="table-card">
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            current,
            pageSize,
            total,
            ...PAGINATION_CONFIG.simple,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size);
            },
          }}
        />
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑资讯' : '新增资讯'}
        open={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          // 延迟重置，避免关闭时闪烁
          setTimeout(() => {
            editorContentRef.current = '';
          }, 300);
        }}
        width={900}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
        destroyOnHidden={false}
        getContainer={() => document.body}
        maskClosable={false}
        zIndex={1000}
        forceRender={false}
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          labelAlign="left"
          initialValues={{
            status: 1,
            tags: [],
          }}
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入新闻标题" />
          </Form.Item>

          <Form.Item
            name="summary"
            label="摘要"
          >
            <TextArea rows={3} placeholder="请输入新闻摘要（可选）" />
          </Form.Item>

          <Form.Item
            name="content"
            label="内容"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <CustomRichTextEditor
              ref={editorRef} 
              placeholder="请输入新闻内容"
              onChange={(content) => {
                editorContentRef.current = content;
                // 同步到表单
                form.setFieldsValue({ content });
              }}
              value={form.getFieldValue('content') || editorContentRef.current}
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="category"
              label="分类"
              rules={[{ required: true, message: '请选择分类' }]}
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <DictSelect
                dictType={DICT_TYPES.NEWS_CATEGORY}
                placeholder="请选择分类"
              />
            </Form.Item>

            <Form.Item
              name="author"
              label="作者"
              rules={[{ required: true, message: '请输入作者' }]}
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Input placeholder="请输入作者" />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="publishTime"
              label="发布时间"
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="status"
              label="发布状态"
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <DictSelect
                dictType={DICT_TYPES.NEWS_STATUS}
                placeholder="请选择发布状态"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="tags"
            label="标签"
          >
            <Select mode="tags" placeholder="请输入标签">
              {DEFAULT_DICT_DATA[DICT_TYPES.NEWS_TAG]?.map(tag => (
                <Select.Option key={tag.dictValue} value={tag.dictLabel}>
                  {tag.dictLabel}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="coverImage"
            label="封面图片"
          >
            <PictureUpload
              maxCount={1}
              previewTitle="封面图片预览"
            />
          </Form.Item>

          <Form.Item
            label="附件"
          >
            <AttachmentUpload
              ref={attachmentUploadRef}
              value={editingRecord?.attachmentUrls ? JSON.parse(editingRecord.attachmentUrls) : []}
              onChange={(urls) => {
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default NewsManagement;
